import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { LiveMap } from "@/components/LiveMap";
import { fmtDateTime } from "@/lib/format";
import { Bike, MapPin } from "lucide-react";

const LABEL = { assigned: "Assigned", picked_up: "Picked up", out_for_delivery: "Out for delivery" };
const badge = (s) => ({ assigned: "requested", picked_up: "dispatched", out_for_delivery: "dispatched" }[s] || "requested");

export default function LiveMapPage() {
  const [rows, setRows] = useState([]);

  const load = useCallback(() => {
    api.get("/deliveries/live").then((r) => setRows(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const iv = setInterval(load, 12000);
    return () => clearInterval(iv);
  }, [load]);

  const markers = [];
  rows.forEach((d) => {
    if (d.rider_location) markers.push({ lat: d.rider_location.latitude, lng: d.rider_location.longitude, color: "#2563eb", label: `${d.rider_name || "Rider"} · ${d.invoice_no}` });
    if (d.dest_lat != null) markers.push({ lat: d.dest_lat, lng: d.dest_lng, color: "#e11d48", label: `${d.customer_name || "Destination"} · ${d.invoice_no}` });
  });

  return (
    <div>
      <PageHeader title="Live map" subtitle="Real-time rider positions and delivery destinations across all branches" />
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-2 shadow-sm">
          <LiveMap markers={markers} height={520} />
          <div className="flex items-center gap-4 px-2 py-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: "#2563eb" }} /> Rider</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: "#e11d48" }} /> Destination</span>
          </div>
        </Card>
        <Card className="p-4 shadow-sm">
          <div className="text-sm font-semibold mb-3 flex items-center gap-2"><Bike className="h-4 w-4" /> Active deliveries ({rows.length})</div>
          <div className="space-y-3 max-h-[520px] overflow-y-auto">
            {rows.length === 0 && <div className="text-center text-muted-foreground py-10 text-sm">No active deliveries right now</div>}
            {rows.map((d) => (
              <div key={d.id} className="border-b border-border pb-3 last:border-0" data-testid={`live-row-${d.invoice_no}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-muted-foreground">{d.invoice_no}</span>
                  <StatusBadge value={badge(d.status)} label={LABEL[d.status]} />
                </div>
                <div className="text-sm font-medium mt-0.5">{d.customer_name}</div>
                <div className="flex items-start gap-1.5 text-xs text-muted-foreground mt-0.5"><MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />{d.address || "—"}</div>
                <div className="text-xs text-muted-foreground mt-1">Rider: {d.rider_name || "Unassigned"}{d.rider_location ? ` · updated ${fmtDateTime(d.rider_location.at)}` : " · no live location yet"}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
